import {
  add,
  sub,
  addDays,
  subDays,
  addMinutes,
  subMinutes,
  differenceInSeconds,
  differenceInMinutes,
  differenceInDays,
  // differenceInCalendarDays,
  isSameMonth,
  isValid,
  isDate,
  toDate,
  isBefore,
  isAfter,
  isToday,
  isTomorrow,
  startOfToday,
  // startOfDay,
  getISODay,
} from "date-fns";

const usedDefaultFns = {
  add,
  sub,
  addDays,
  subDays,
  addMinutes,
  subMinutes,
  differenceInSeconds,
  differenceInMinutes,
  differenceInDays,
  // differenceInCalendarDays,
  isSameMonth,
  isValid,
  toDate,
  isDate,
  isBefore,
  isAfter,
  isToday,
  isTomorrow,
  startOfToday,
  // startOfDay,
  getISODay,
};

import {
  format as _format,
  parse as _parse,
  parseISO as _parseISO,
  formatDistance as _formatDistance,
  formatDistanceStrict as _formatDistanceStrict,
  formatRelative as _formatRelative,
  startOfWeek as _startOfWeek,
  endOfWeek as _endOfWeek,
} from "date-fns";

import { et, enUS, ru, fi } from "date-fns/locale";
const locales = {
  et,
  ru,
  fi,
  en: enUS,
};

/**
 * We can not use `this` in DateFns class because this is undefined
 * if the class method is called in async function!
 * */
function getLocale() {
  const lang = document.querySelector("html").getAttribute("lang");
  const locale = locales[lang];
  return locale ? locale : enUS;
}

const weekStartsOn = 1;

const formats = {
  date: "yyyy-MM-dd",
  dateTime: "yyyy-MM-dd HH:mm",
  time: "HH:mm",
  dateTimeForHumans: "HH:mm yyyy-MM-dd",
  dateForHumans: "dd.MM.yyyy",
};

const formatSlugs = Object.keys(formats);

const DEFAULT_FORMAT = formats.dateTime;
formats.default = DEFAULT_FORMAT;

export const format = function(date, outputFormat = DEFAULT_FORMAT, locale = null) {
  locale = getLocale();

  if (!isValid(date)) {
    console.warn("Date is not date object. Date:", date);
  }

  if (formatSlugs.includes(outputFormat)) outputFormat = formats[outputFormat];

  return _format(date, outputFormat, { locale: locale });
};

export const parse = function(
  parseable,
  format = DEFAULT_FORMAT,
  date = new Date(),
  locale = null
) {
  if (!locale) locale = getLocale();
  return _parse(parseable, format, date, { locale });
};

export const parseISO = function(parseable) {
  if (parseable === "" || parseable === null) {
    console.warn(
      "You are passing empty string or null to date-fns-customized-functions/parseISO(). Defaulted to new Date()."
    );
    return new Date();
  }
  if (isDate(parseable)) {
    console.warn(
      "You are passing date object to date-fns-customized-functions/parseISO(). It's better to avoid this so we can skip type check in production.",
      parseable
    );
    return parseable;
  }

  return _parseISO(parseable);
};

export const formatDistance = function(date, baseDate, options = {}) {
  options = { locale: getLocale(), ...options };
  return _formatDistanceStrict(date, baseDate, options);
};

export const formatDistanceStrict = function(date, baseDate, options = {}) {
  options = { locale: getLocale(), ...options };
  return _formatDistanceStrict(date, baseDate, options);
};

export const formatRelative = function(date, baseDate, options = {}) {
  // options.locale = et
  options.weekStartsOn = weekStartsOn;
  return _formatRelative(date, baseDate, options);
};

export const formatParse = function(
  parseable,
  outputFormat,
  parseFormat = null,
  date = null,
  locale = null
) {
  if (!locale) locale = getLocale();

  if ((parseFormat === date) === null) return formatParseISO(parseable, outputFormat, locale);

  const parsedDate = parse(parseable, parseFormat, date, locale);
  return format(parsedDate, outputFormat, locale);
};

export const formatParseISO = function(parseable, outputFormat, locale = null) {
  if (!locale) locale = getLocale();

  const parsedDate = parseISO(parseable);
  return format(parsedDate, outputFormat, locale);
};

export const startOfWeek = function(date, locale = null) {
  if (!locale) locale = getLocale();
  return _startOfWeek(date, { locale, weekStartsOn });
};

export const endOfWeek = function(date, locale = null) {
  if (!locale) locale = getLocale();
  return _endOfWeek(date, { locale, weekStartsOn });
};

export const formatDate = function(date) {
  return format(date, formats.date);
};

const customizedFns = {
  format,
  parse,
  parseISO,
  formatDistance,
  formatDistanceStrict,
  formatRelative,
  formatParse,
  formatParseISO,
  startOfWeek,
  endOfWeek,
  formatDate,
};

/**
 * Test if fetchedAt is older than minutes.
 * @param {date} fetchedAt
 * @param {int} minutes
 * @returns
 */
function shouldRefetch(fetchedAt, minutes = 10) {
  if ($date.isValid(fetchedAt) && $date.differenceInMinutes(new Date(), fetchedAt) < minutes) {
    return false;
  }

  return true;
}

const extraFns = {
  shouldRefetch,
};

class DateFns {
  constructor() {
    this.formats = formats;
    for (let [key, value] of Object.entries(usedDefaultFns)) {
      this[key] = value;
    }
    for (let [key, value] of Object.entries(customizedFns)) {
      this[key] = value;
    }
    for (let [key, value] of Object.entries(extraFns)) {
      this[key] = value;
    }
  }
}

const $date = new DateFns();
export default $date;
